import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ImageField } from "./ImageField";
import { ObjectFields } from "./ObjectFields";
import { ArrayEditor } from "./ArrayEditor";
import { specFor } from "@/lib/imageSpecs";

// Image fields: any key that names a picture (image, rawImage, heroImg, poster, logo, avatar, …),
// or any value that is an image link. "icon" only when it holds a link (it is often an icon name).
const IMG_KEY = /^(image|img|image_url|imageurl|photo|icon|bg|background|cover|thumbnail|src)$/i;
const IMG_KEY_ANY = /(image|img|photo|poster|logo|avatar|thumb|banner|background|picture|cover)(_?url)?$/i;
const NOT_IMG_KEY = /(alt|caption|label|title|text|position|fit|size|count)$/i;
const IMG_VAL = /\.(png|jpe?g|webp|gif|svg|avif)(\?|$)|images\.unsplash\.com|prod-images|firebasestorage/i;
const imgKey = (k) => !NOT_IMG_KEY.test(k) && (IMG_KEY.test(k) || IMG_KEY_ANY.test(k)) && !/^icon$/i.test(k);
const LONG_KEY = /^(copy|d|lead|description|desc|body|text|subtitle|note|feeNote)$/i;
// A filled value must look like a link/path (colours like "#fff" or words are not images).
const linkish = (v) => v === "" || /[/.]/.test(v);
const isImage = (k, v) => typeof v === "string" && linkish(v) && (imgKey(k) || IMG_VAL.test(v) || (/^icon$/i.test(k) && /^(https?:|\/)/.test(v)));
export const blankLike = (x) => (Array.isArray(x) ? [] : x && typeof x === "object" ? Object.fromEntries(Object.entries(x).map(([k, v]) => [k, blankLike(v)])) : typeof x === "number" ? 0 : typeof x === "boolean" ? false : "");
export const tid = (p) => p.replace(/[^a-zA-Z0-9_-]/g, "-");

// Recursive editor for any JSON-like value from site_content.
export function FieldEditor({ name, value, onChange, path, template, hints }) {
  if (isImage(name, value) || (imgKey(name) && value === "")) return <ImageField value={value} onChange={onChange} spec={specFor(path, name)} testId={`cms-img-${tid(path)}`} />;
  if (typeof value === "boolean") return <Switch checked={value} onCheckedChange={onChange} data-testid={`cms-bool-${tid(path)}`} />;
  if (typeof value === "number") return <Input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} className="h-8 w-32" data-testid={`cms-num-${tid(path)}`} />;
  if (typeof value === "string") {
    const long = LONG_KEY.test(name) || value.length > 70;
    return long
      ? <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={Math.min(5, Math.ceil(value.length / 90) + 1)} data-testid={`cms-text-${tid(path)}`} />
      : <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-9" data-testid={`cms-text-${tid(path)}`} />;
  }
  if (Array.isArray(value)) return <ArrayEditor value={value} onChange={onChange} path={path} template={template} hints={hints} />;
  if (value && typeof value === "object") return <ObjectFields obj={value} onChange={onChange} path={path} hints={hints} />;
  return <Input value="" placeholder="(empty)" onChange={(e) => onChange(e.target.value)} className="h-8" data-testid={`cms-text-${tid(path)}`} />;
}

